import { useMutation } from "@tanstack/react-query";
import { markFeedbackReviewed, type ApiError } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";

/** Body for a "Mark done" / response-signal POST. Exactly one target
 * must be set; the optional `response_signal` + `reflection` carry the
 * player's reaction (Phase B: the signal is its own field, no longer a
 * `[SIGNAL:*]` reflection prefix). */
export type MarkReviewedBody = {
  note_id?: number;
  playlist_id?: number;
  /** PR 7: clip review target. */
  clip_id?: number;
  /** Phase B: mark a match recap (summary) read. */
  summary_id?: number;
  reflection?: string;
  /** Phase B: structured player response signal. `""` clears; omit to
   * preserve a previously-stored one. */
  response_signal?: "got_it" | "need_help" | "tried_this" | "";
};

/**
 * "Mark done" mutation for the `/feedback` surfaces.
 *
 * Marks a note, playlist, clip, or match recap done via
 * `POST /api/my-feedback/review`. Exactly one target must be set
 * (router returns 422 otherwise). On success it invalidates the
 * `["my-feedback"]` bundle so the Reviewed/New pills refresh.
 *
 * The backend re-validates visibility at mark-time, so a guessed
 * id 403s rather than recording a review — the client doesn't
 * need to pre-check.
 */
export function useFeedbackReview() {
  const mutation = useMutation({
    mutationFn: (body: MarkReviewedBody) => markFeedbackReviewed(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-feedback"] });
    },
  });
  return {
    markReviewed: (body: MarkReviewedBody) => mutation.mutateAsync(body),
    isPending: mutation.isPending,
    error: (mutation.error as ApiError | null) ?? null,
  };
}
